import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { getRecentPosts } from '@/config/blogPosts';

export default function BlogPage() {
  const posts = getRecentPosts(10);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div>
      {/* Hero Section */}
      <section 
        data-testid="blog-hero"
        className="relative pt-28 pb-16 bg-slate-50"
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase mb-3">
              Blog & Resources
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
              Education Insights
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Explore articles about education in the Philippines, top universities, 
              and opportunities for students pursuing their dreams.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {posts.length > 0 && (
        <section data-testid="featured-post" className="py-12 bg-white">
          <div className="container mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src={posts[0].image}
                  alt={posts[0].title}
                  className="w-full h-[320px] object-cover"
                />
              </div>
              <div>
                <div className="flex items-center gap-2 text-slate-500 mb-3">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{formatDate(posts[0].date)}</span>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">
                  {posts[0].title}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-5">
                  {posts[0].excerpt}
                </p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {posts[0].tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/blog/${posts[0].slug}`}
                  data-testid="featured-post-link"
                  className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-md px-5 py-2.5 font-medium transition-colors"
                >
                  Read Article
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section data-testid="blog-grid" className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">
            All Articles
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <article
                key={post.id}
                data-testid={`blog-post-${post.slug}`}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-slate-500 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs">{formatDate(post.date)}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-3 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-800 font-medium text-sm transition-colors"
                  >
                    Read more
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="blog-cta" className="py-16 bg-slate-800">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Stay Informed
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-8">
            Subscribe to our newsletter for the latest updates on education 
            opportunities and foundation news.
          </p>
          <Link
            to="/contact"
            data-testid="blog-subscribe-cta"
            className="inline-flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md px-6 py-3 font-medium transition-colors"
          >
            Subscribe to Updates
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
